import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../../../../components/base/PageLayout';
const Posture: React.FC = () => {
  React.useEffect(() => {
    document.title = '美しい所作 - 執事が教える立ち居振る舞いと姿勢の整え方｜執事ベル';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '執事ベルが伝授する美しい所作と立ち居振る舞い。正しい姿勢の保ち方、優雅な歩き方、エレガントなお辞儀、物の受け渡し方、椅子への座り方など、日常の動作を洗練させる具体的なポイントを解説。第一印象を決める所作の重要性と、毎日の習慣で自然と身につける方法をお伝えします。面接、商談、接客など、人前に立つすべての方に役立つ内容です。');
    }
  }, []);


  return (
    <PageLayout hero={<>
<div className="relative h-96 bg-gradient-to-r from-gray-900 to-gray-700 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
            style={{
              backgroundImage: `url('/images/generated/seq-posture-hero-ff6a4f.jpg')`
            }}
          />
          <div className="relative z-10 text-center text-white max-w-6xl mx-auto px-6">
            <h1 className="text-5xl md:text-6xl font-serif mb-6 tracking-wide">
              美しい所作
            </h1>
            <p className="text-xl md:text-2xl font-light italic opacity-90">
              The Art of Graceful Movement
            </p>
          </div>
        </div>
</>}>
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        

        {/* Article Content */}
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <div className="mb-12 p-8 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border-l-4 border-amber-600">
              <p className="text-lg leading-relaxed text-gray-800 mb-0">
                美しい所作は、執事の品格を表現する最も重要な要素の一つです。立ち居振る舞い、歩き方、物の扱い方、すべてに品位と優雅さが求められます。これらは単なる形式ではなく、相手への敬意と自分自身への誇りを表現する手段なのです。真のプロフェッショナルとしての美しい所作を身につける方法をご紹介いたします。
              </p>
            </div>

            {/* Main Content */}
            <div className="space-y-12">
              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  所作の基本哲学
                </h2>
                <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      美しい所作とは、外見的な美しさだけを追求するものではありません。それは、内面の品格と教養が自然に表れた結果です。相手への敬意、物への感謝、そして自分自身への誇りが、美しい動作として現れるのです。
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      執事の所作は、お客様に安心感と信頼感を与える重要な要素です。洗練された動作は、プロフェッショナルとしての能力を無言で伝え、サービス全体の品質を向上させます。
                    </p>
                  </div>
                  <div className="relative">
                    <img 
                      src="/images/generated/seq-posture-philosophy-9ea7ba.jpg"
                      alt="所作の哲学"
                      className="w-full h-80 object-cover rounded-lg shadow-lg"
                    />
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">美しい所作の三要素</h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-amber-700 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-user-line text-2xl"></i>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">姿勢の美しさ</h4>
                      <p className="text-gray-700 text-sm">背筋を伸ばした品格ある立ち姿</p>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-amber-700 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-footprint-line text-2xl"></i>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">動作の流れ</h4>
                      <p className="text-gray-700 text-sm">無駄のない滑らかな動き</p>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-amber-700 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-hand-heart-line text-2xl"></i>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">手の使い方</h4>
                      <p className="text-gray-700 text-sm">丁寧で優雅な手の動作</p>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  基本姿勢の習得
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  美しい所作の基盤となるのは、正しい基本姿勢です。立ち方、座り方、歩き方の基本をマスターすることで、すべての動作に品格が宿ります。
                </p>
                
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-blue-800 mb-4">
                      <i className="ri-user-3-line text-blue-600 mr-2"></i>
                      正しい立ち姿勢
                    </h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-blue-800 mb-3">基本ポイント</h4>
                        <ul className="space-y-2 text-blue-700">
                          <li>• 頭頂部を天井に向けて引き上げる</li>
                          <li>• 肩の力を抜き、自然に下げる</li>
                          <li>• 背筋を真っ直ぐに保つ</li>
                          <li>• 重心を両足に均等に配分</li>
                          <li>• 足は肩幅程度に開く</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-blue-800 mb-3">注意点</h4>
                        <ul className="space-y-2 text-blue-700">
                          <li>• 胸を張りすぎない</li>
                          <li>• 膝を突っ張らない</li>
                          <li>• 顎を引きすぎない</li>
                          <li>• 手は自然に体側に</li>
                          <li>• 呼吸は深く静かに</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-green-800 mb-4">
                      <i className="ri-armchair-line text-green-600 mr-2"></i>
                      品格ある座り方
                    </h3>
                    <div className="space-y-4">
                      <div className="bg-white border border-green-300 rounded p-4">
                        <h4 className="font-semibold text-green-800 mb-2">椅子への着席</h4>
                        <p className="text-green-700 mb-3">椅子の前に立ち、背もたれに軽く触れてから、ゆっくりと腰を下ろします。</p>
                        <div className="grid md:grid-cols-2 gap-4">
                          <ul className="space-y-1 text-green-700">
                            <li>• 椅子の3分の2程度に座る</li>
                            <li>• 背筋を伸ばして背もたれから離す</li>
                            <li>• 膝は90度に保つ</li>
                          </ul>
                          <ul className="space-y-1 text-green-700">
                            <li>• 足は床にしっかりとつける</li>
                            <li>• 手は膝の上または肘掛けに</li>
                            <li>• 肩の力を抜いてリラックス</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  優雅な歩き方
                </h2>
                <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
                  <div className="relative">
                    <img 
                      src="/images/generated/seq-posture-walking-21392f.jpg"
                      alt="優雅な歩き方"
                      className="w-full h-80 object-cover rounded-lg shadow-lg"
                    />
                  </div>
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      歩き方は、その人の品格を最も表現する動作の一つです。執事の歩き方は、安定感がありながらも軽やかで、周囲に安心感を与える必要があります。
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      急がず、遅すぎず、一定のリズムを保ちながら歩くことで、プロフェッショナルとしての落ち着きと信頼性を表現できます。
                    </p>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">美しい歩行のポイント</h3>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3 text-amber-600">上半身</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li>• 頭は真っ直ぐに保つ</li>
                        <li>• 視線は前方15-20m先に</li>
                        <li>• 肩は水平に保ち、力を抜く</li>
                        <li>• 腕は自然に振る</li>
                        <li>• 背筋を伸ばして歩く</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3 text-amber-600">下半身</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li>• かかとから着地する</li>
                        <li>• 歩幅は肩幅程度に保つ</li>
                        <li>• 膝は軽く曲げて歩く</li>
                        <li>• 足音を立てすぎない</li>
                        <li>• 一定のリズムを維持</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  物の扱い方
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  執事にとって、物の扱い方は技術的なスキルであると同時に、品格の表現でもあります。すべての物を大切に扱い、丁寧な動作を心がけることで、プロフェッショナルとしての姿勢を示します。
                </p>
                
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                    <div className="w-12 h-12 bg-amber-700 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-cup-line text-xl"></i>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">食器の扱い</h3>
                    <p className="text-gray-700 text-sm">両手で丁寧に、音を立てずに扱う</p>
                  </div>
                  
                  <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                    <div className="w-12 h-12 bg-amber-700 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-door-line text-xl"></i>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">ドアの開閉</h3>
                    <p className="text-gray-700 text-sm">静かで滑らかな動作を心がけます。手前に引くドアではお客様を先にお通しし、押して開けるドアでは自分が先に入って脇でドアを支えます。ノブから手を離さず、最後まで音を立てずに閉めます</p>
                  </div>
                  
                  <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                    <div className="w-12 h-12 bg-amber-700 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-file-text-line text-xl"></i>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">書類の取り扱い</h3>
                    <p className="text-gray-700 text-sm">整理整頓を保ち、丁寧に扱う</p>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-8 rounded-lg mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">サービス時の所作</h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-amber-700 text-white rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <i className="ri-check-line text-sm"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">お辞儀の仕方</h4>
                        <p className="text-gray-700">お辞儀は角度によって三つに分かれます。軽い挨拶やすれ違いの「会釈」は上体を約15度、日常の業務で最もよく用いる「普通礼」は約30度、深い感謝やお詫びを伝える「敬礼」は約45度。いずれも背筋を伸ばしたまま腰から上体を倒し、視線は自然に足元へ落とします。普通礼と敬礼では、先に言葉を伝えてから礼に移り、下げたところで一呼吸置き、ゆっくりと戻すと所作が美しく整います</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-amber-700 text-white rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <i className="ri-check-line text-sm"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">物の受け渡し</h4>
                        <p className="text-gray-700">両手で丁寧に、相手が受け取りやすい位置と角度で</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-amber-700 text-white rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <i className="ri-check-line text-sm"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">待機姿勢</h4>
                        <p className="text-gray-700">背筋を伸ばし、手は前で軽く組む。いつでも動ける準備を</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  日常的な練習方法
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  美しい所作は一朝一夕に身につくものではありません。日常生活の中で意識的に練習を重ねることで、自然で美しい動作が身につきます。
                </p>
                
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-blue-800 mb-3">
                      <i className="ri-mirror-line text-blue-600 mr-2"></i>
                      鏡を使った練習
                    </h3>
                    <p className="text-blue-700 leading-relaxed mb-4">
                      毎日鏡の前で基本姿勢を確認し、美しい立ち姿勢、座り方、歩き方を練習します。客観的に自分の姿を見ることで、改善点が明確になります。
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <ul className="space-y-2 text-blue-700">
                        <li>• 朝の身だしなみ時に姿勢チェック</li>
                        <li>• 歩行練習（10分程度）</li>
                        <li>• お辞儀の角度確認</li>
                      </ul>
                      <ul className="space-y-2 text-blue-700">
                        <li>• 手の動きの練習</li>
                        <li>• 表情の確認</li>
                        <li>• 全体的なバランスチェック</li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-green-800 mb-3">
                      <i className="ri-book-line text-green-600 mr-2"></i>
                      本を使った姿勢練習
                    </h3>
                    <p className="text-green-700 leading-relaxed mb-4">
                      頭の上に本を載せて歩く練習は、正しい姿勢を身につける効果的な方法です。本が落ちないよう意識することで、自然と美しい姿勢が身につきます。
                    </p>
                    <div className="bg-white border border-green-300 rounded p-4">
                      <h4 className="font-semibold text-green-800 mb-2">練習手順</h4>
                      <ol className="space-y-1 text-green-700 text-sm list-decimal list-inside">
                        <li>軽い本を頭の上に載せる</li>
                        <li>正しい立ち姿勢を取る</li>
                        <li>本を落とさないよう歩く</li>
                        <li>毎日5-10分継続する</li>
                        <li>慣れてきたら階段でも練習</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  心構えと継続のコツ
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  美しい所作の習得には、技術的な練習だけでなく、適切な心構えも重要です。なぜ美しい所作が必要なのかを理解し、継続的に向上を目指す姿勢を持ちましょう。
                </p>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-amber-800 mb-3">
                    <i className="ri-lightbulb-line mr-2"></i>
                    執事ベルからのアドバイス
                  </h3>
                  <p className="text-amber-700 leading-relaxed">
                    「美しい所作は、相手への敬意の表現です。技術的な完璧さも大切ですが、最も重要なのは『相手に喜んでいただきたい』という真心です。その気持ちがあれば、自然と動作に品格が宿ります。毎日の小さな意識の積み重ねが、やがて自然で美しい所作となって現れるのです。完璧を目指しながらも、自分らしさを失わないことが大切です。」
                  </p>
                </div>
              </section>
            </div>

            {/* Related Articles */}
            <div className="mt-16 pt-12 border-t border-gray-200">
              <h3 className="text-2xl font-serif text-gray-900 mb-8">関連記事</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <a href="/wisdom/articles/speech" className="group block bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <img 
                    src="/images/generated/seq-related-speech-caa38c.jpg"
                    alt="言葉遣いと話し方"
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">言葉遣いと話し方</h4>
                    <p className="text-sm text-gray-600 mt-2">品格ある言葉の使い方</p>
                  </div>
                </a>
                
                <a href="/wisdom/articles/social-etiquette" className="group block bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <img 
                    src="/images/generated/seq-related-social-435417.jpg"
                    alt="社交のエチケット"
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">社交のエチケット</h4>
                    <p className="text-sm text-gray-600 mt-2">社交の場での振る舞い</p>
                  </div>
                </a>
                
                <a href="/wisdom/articles/dress-code" className="group block bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <img 
                    src="/images/generated/seq-related-dress-1048d1.jpg"
                    alt="ドレスコードの基本"
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">ドレスコードの基本</h4>
                    <p className="text-sm text-gray-600 mt-2">場に応じた装いの選び方</p>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Posture;